import { FormEvent, useId, useState } from 'react';
import { checkIsStringSensitiveData } from 'helpers';

/**
 * Longest search that is sent. The field takes more — no `maxLength`, which
 * would stop typing and cut a paste without saying why — and shows the
 * max-length error until the term is back under it.
 */
const MAX_LENGTH = 200;

type TSearchFieldProps = {
  defaultValue?: string;
  placeholder?: string;
  submitLabel?: string;
  /** Overrides the 200-character ceiling. */
  maxLength?: number;
  /**
   * Shown under the field while the term looks like an email, phone or card
   * number. The field still turns red and refuses to submit without it.
   */
  sensitiveAlertText?: string;
  /**
   * Shown under the field while the term is over `maxLength`. `{max}` is
   * replaced with the limit.
   */
  maxLengthAlertText?: string;
  onSubmit: (term: string) => void;
};

function SearchField({
  defaultValue,
  placeholder,
  submitLabel,
  maxLength = MAX_LENGTH,
  sensitiveAlertText,
  maxLengthAlertText,
  onSubmit,
}: TSearchFieldProps) {
  // Ties the input to the error message, so it is read with it.
  const alertId = useId();
  const [value, setValue] = useState(defaultValue || '');

  // Personal data never leaves the browser, and neither does a term over the
  // limit: while either applies the field is marked invalid and submitting
  // does nothing.
  const isSensitive = checkIsStringSensitiveData(value);
  const isTooLong = value.length > maxLength;
  const hasError = isSensitive || isTooLong;

  // One message at a time, in the same place. Personal data matters more than
  // length, so it wins when both apply.
  const errorText = isSensitive
    ? sensitiveAlertText
    : isTooLong
      ? (maxLengthAlertText || '').replace('{max}', String(maxLength))
      : '';

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (hasError) {
      return;
    }

    onSubmit(value.trim());
  };

  return (
    <>
      <form
        className={['m-search-field', hasError ? 'm-search-field--error' : '']
          .filter(Boolean)
          .join(' ')}
        role="search"
        onSubmit={handleSubmit}
      >
        <div className="m-search-field__box-main">
          <span className="m-search-field__search-icon">
            <i className="zmdi zmdi-search" aria-hidden="true" />
          </span>
          <input
            type="text"
            name="term"
            className="m-search-field__input"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={placeholder}
            aria-label={placeholder}
            aria-invalid={hasError}
            aria-describedby={errorText ? alertId : undefined}
            autoComplete="off"
          />
        </div>
        <button
          type="submit"
          className="m-search-field__submit"
          aria-label={submitLabel}
        >
          <span className="m-search-field__submit-label">{submitLabel}</span>
          <span className="m-search-field__submit-icon">
            <i className="zmdi zmdi-search" aria-hidden="true" />
          </span>
        </button>
      </form>

      {errorText ? (
        <div id={alertId} className="m-search-field__alert" role="alert">
          {errorText}
        </div>
      ) : null}
    </>
  );
}

export default SearchField;
